/**
 * Flatpickr Year Select Plugin.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface PluginConfig {
  text?: string;
  theme?: string;
  selectedYear?: string;
  defaultYear?: string;
  yearStart?: number;
  yearEnd?: number;
}

export const yearDropdownPlugin = function (pluginConfig: PluginConfig = {}): (fp: any) => any {
  const defaultConfig: Required<PluginConfig> = {
    text: '',
    theme: 'light',
    selectedYear: dayjs().utc(true).subtract(18, 'years').utc(true).format('MM/DD/YYYY').toString(),
    defaultYear: '',
    yearStart: dayjs().utc(true).year() - 18,
    yearEnd: dayjs().utc(true).year() + 50,
  };

  const config: Required<PluginConfig> = { ...defaultConfig, ...pluginConfig };

  const yearDropdown: HTMLSelectElement = document.createElement('select');
  const createSelectElement = (): void => {
    const start = config.yearEnd;
    const end = config.yearStart;
    const selected = config.selectedYear || start.toString();
    for (let i = end; i <= start; i++) {
      const option = document.createElement('option');
      option.value = i.toString();
      option.text = i.toString();
      yearDropdown.appendChild(option);
    }

    if (yearDropdown.querySelector(`option[value="${selected}"]`)) {
      yearDropdown.value = selected;
    } else {
      yearDropdown.value = start.toString(); // fallback
    }
  };

  return function (fp: any) {
    fp.yearSelectContainer = fp._createElement(
      'div',
      `flatpickr-year-select ${config.theme}Theme`,
      config.text
    );

    fp.yearSelectContainer.tabIndex = -1;
    createSelectElement();
    yearDropdown.addEventListener('change', (evt: Event) => {
      const target = evt.target as HTMLSelectElement;
      const year = target.options[target.selectedIndex].value;
      fp.changeYear(parseInt(year, 10));
    });

    config.defaultYear && (yearDropdown.value = config.defaultYear);
    fp.yearSelectContainer.append(yearDropdown);

    return {
      onReady: (selectedDates: Date[]) => {
        fp.changeYear(parseInt(yearDropdown.value, 10));
        const name = fp.monthNav.className;
        const yearInputCollection = fp.calendarContainer.getElementsByClassName(name);
        const el = yearInputCollection[0] as HTMLElement;
        el.parentNode?.insertBefore(fp.yearSelectContainer, el.parentNode.firstChild);

        if (selectedDates && selectedDates.length > 0) {
          const selectedDate = selectedDates[0];
          const selectedYear = selectedDate?.getFullYear();
          if (
            selectedYear &&
            Array.from(yearDropdown.options).some((option) => Number(option.value) === selectedYear)
          ) {
            // If the selected/typed year is in the dropdown options
            yearDropdown.value = selectedYear.toString();
          } else {
            // If the selected/typed year is NOT in the dropdown options, we select the last option of the dropdown
            yearDropdown.value = config.yearEnd.toString();
            fp.changeYear(config.yearEnd);
          }
        }
      },
    };
  };
};

export default yearDropdownPlugin;
